import { MouseEvent, ReactNode } from "react"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    header: {
        padding: "1.25rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    title: {
        color: theme.palette.tailwind.slate[900],
    },
    orderNumber: {
        color: theme.palette.tailwind.slate[500],
    },
    headerActionButton: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".75rem",
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[600],
    },
}))

export type OrderDrawerHeaderAction = Readonly<{
    icon: IconDefinition
    ariaLabel: string
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void
}>

type Props = Readonly<{
    title?: string
    orderId?: number
    subtitle?: ReactNode
    actions: ReadonlyArray<OrderDrawerHeaderAction>
    onClose: () => void
}>

export function OrderDrawerHeader({ title, orderId, subtitle, actions, onClose }: Props) {
    const { classes } = useStyles()

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing="1rem"
            className={classes.header}
        >
            <Box minWidth={0}>
                <Typography variant="h5" fontWeight={700} className={classes.title} noWrap>
                    {title}
                </Typography>
                {subtitle ??
                    (orderId != null && (
                        <LocalizedStrict
                            id="meeting-service-order-drawer-order-number"
                            vars={{ number: String(orderId) }}
                        >
                            <Typography variant="body2" className={classes.orderNumber}>
                                {`Order #${orderId}`}
                            </Typography>
                        </LocalizedStrict>
                    ))}
            </Box>
            <Stack direction="row" alignItems="center" spacing=".5rem" flexShrink={0}>
                {actions.map((action) => (
                    <IconButton
                        key={action.ariaLabel}
                        size="small"
                        aria-label={action.ariaLabel}
                        className={classes.headerActionButton}
                        onClick={action.onClick}
                    >
                        <FontAwesomeIcon icon={action.icon} size="sm" />
                    </IconButton>
                ))}
                <IconButton size="small" aria-label="close" className={classes.headerActionButton} onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} size="sm" />
                </IconButton>
            </Stack>
        </Stack>
    )
}
